/**
 * Booking client. Two backends with the same interface:
 *  - "backend":    Supabase Edge Functions (server decides test vs live payments)
 *  - "simulation": in-browser simulator, LOCAL DEV SERVER ONLY, clearly labelled
 *  - "unavailable": production build without a configured backend: no online booking
 * The browser never sends prices: the server computes every amount.
 */
import { callFunction, callRpc, isBackendConfigured, isDevSimulation } from "./config";
import { createBookingSimulator } from "./bookingSimulator";
import { TERMS_VERSION, REFUND_POLICY_VERSION } from "../data/legal";

/** Public Turnstile site key (not a secret). Empty = no widget (e.g. dev simulation). */
export const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

/** Simulated test capacity used ONLY by the local dev simulator (not a real number). */
export const SIMULATED_TEST_CAPACITY = 40;

export function getBookingMode() {
  if (isBackendConfigured()) return "backend";
  if (isDevSimulation()) return "simulation";
  return "unavailable";
}

const simulators = new Map();
function simulatorFor(event) {
  if (!simulators.has(event.slug)) {
    simulators.set(
      event.slug,
      createBookingSimulator({
        capacity: SIMULATED_TEST_CAPACITY,
        priceCents: Math.round(event.price.amount * 100),
        currency: event.price.currency,
      })
    );
  }
  return simulators.get(event.slug);
}

class BookingError extends Error {
  constructor(code, detail) {
    super(code);
    this.code = code;
    this.detail = detail;
  }
}

const unwrap = (res) => {
  if (res.ok) return res.data;
  throw new BookingError(res.data?.error || `http_${res.status}`, res.data?.detail);
};

const wrapSim = (fn) => {
  try {
    return Promise.resolve(fn());
  } catch (e) {
    return Promise.reject(new BookingError(e.code || "error", e.detail));
  }
};

export async function fetchAvailability(event) {
  const mode = getBookingMode();
  if (mode === "backend") return callRpc("get_event_availability", { p_slug: event.slug });
  if (mode === "simulation") return simulatorFor(event).availability();
  return null;
}

export async function createBooking(event, { guests, name, email, phone, idempotencyKey, accessToken, turnstileToken, policiesAccepted }) {
  const mode = getBookingMode();
  // The customer must tick the Terms / Refund Policy agreement before paying.
  if (!policiesAccepted) throw new BookingError("policies_not_accepted");
  if (mode === "backend") {
    return unwrap(
      await callFunction("create-booking", {
        event_slug: event.slug,
        guests,
        name,
        email,
        phone: phone || null,
        idempotency_key: idempotencyKey,
        access_token: accessToken,
        turnstile_token: turnstileToken || null,
        accepted_policies: { terms: TERMS_VERSION, refund: REFUND_POLICY_VERSION },
      })
    );
  }
  if (mode === "simulation") {
    return wrapSim(() => simulatorFor(event).reserve({ guests, name, email, phone, idempotencyKey, accessToken }));
  }
  throw new BookingError("online_booking_unavailable");
}

export async function getBooking(event, reference, accessToken) {
  const mode = getBookingMode();
  if (mode === "backend") return unwrap(await callFunction("verify-payment", { reference, access_token: accessToken }));
  if (mode === "simulation") return wrapSim(() => simulatorFor(event).get(reference, accessToken));
  throw new BookingError("online_booking_unavailable");
}

/** Test payments only (server refuses this in live mode). */
export async function simulateTestPayment(event, reference, accessToken, outcome) {
  const mode = getBookingMode();
  if (mode === "backend") {
    return unwrap(await callFunction("simulate-test-payment", { reference, access_token: accessToken, outcome }));
  }
  if (mode === "simulation") return wrapSim(() => simulatorFor(event).pay(reference, accessToken, outcome));
  throw new BookingError("online_booking_unavailable");
}

// --- Per-attempt secrets ------------------------------------------------------

/** URL-safe random token (43 chars). */
export function randomToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const accessKey = (reference) => `gw-booking-${reference}`;

/** The status token stays in this browser tab session only (never in URLs). */
export function rememberBookingAccess(reference, eventSlug, accessToken) {
  try {
    sessionStorage.setItem(accessKey(reference), JSON.stringify({ eventSlug, accessToken }));
  } catch {
    /* storage unavailable: status page will ask the customer to use their email */
  }
}

export function recallBookingAccess(reference) {
  try {
    return JSON.parse(sessionStorage.getItem(accessKey(reference)) || "null");
  } catch {
    return null;
  }
}

/** Human-readable messages for server error codes. */
export function bookingErrorMessage(err) {
  switch (err?.code) {
    case "insufficient_capacity":
      return err.detail !== undefined && Number(err.detail) > 0
        ? `Sorry, only ${err.detail} place${Number(err.detail) === 1 ? "" : "s"} left. Please reduce the number of guests.`
        : "Sorry, this event is now fully booked.";
    case "sales_not_open":
      return "Online booking for this event is not open yet.";
    case "sales_closed":
      return "Online booking for this event has closed.";
    case "invalid_guest_count":
      return "Please choose a valid number of guests.";
    case "invalid_input":
      return "Please check your details and try again.";
    case "booking_not_found":
      return "We couldn't find this booking in this browser. Please open it on the device you booked on, or contact us.";
    case "missing_token":
    case "invalid_token":
    case "token_expired_or_used":
    case "hostname_mismatch":
    case "action_mismatch":
    case "bot_check_failed":
      return "The security check expired or failed. Please complete it again, then retry.";
    case "policies_not_accepted":
      return "Please confirm that you agree to the Terms & Conditions and the Refund & Cancellation Policy.";
    case "verification_unavailable":
      return "The security check is temporarily unavailable. Please try again in a moment.";
    case "online_booking_unavailable":
    case "ticketing_not_configured":
      return "Online booking is not available yet.";
    default:
      return "Something went wrong. Please try again, or contact us.";
  }
}

/** "Email me my ticket": the ticket only ever goes to the email on the booking. Same answer whether or not it matches. */
export async function requestTicketEmail({ reference, email, turnstileToken, website = "" }) {
  if (getBookingMode() !== "backend") throw new BookingError("online_booking_unavailable");
  return unwrap(await callFunction("resend-ticket", { reference: reference.trim(), email: email.trim(), turnstile_token: turnstileToken, website }));
}

/** One ticket's public details (from its link). The token is the access proof; nothing personal is returned. */
export async function viewTicket(token) {
  return unwrap(await callFunction("ticket-view", { token }));
}
